import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';

function Security() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [policy, setPolicy] = useState({
    min_length: 12,
    max_length: 128,
    require_uppercase: true,
    require_lowercase: true,
    require_digits: true,
    require_special: true,
    max_repeated_chars: 4,
  });

  const [lockout, setLockout] = useState({ max_failed_attempts: 10 });
  const [require2fa, setRequire2fa] = useState('off');

  const token = localStorage.getItem('token');
  const headers = { 'access_token': `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${Config.APIBaseURL}/settings/security`, { headers, withCredentials: true }),
      axios.get(`${Config.APIBaseURL}/settings/security/lockout`, { headers, withCredentials: true }),
      axios.get(`${Config.APIBaseURL}/settings/security/require-2fa`, { headers, withCredentials: true }),
    ])
      .then(([pol, lock, req]) => {
        setPolicy(pol.data);
        setLockout(lock.data);
        setRequire2fa(req.data.require_2fa);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load security settings'))
      .finally(() => setLoading(false));
  }, []);

  const flashFeedback = (msg, isError = false) => {
    setFeedback({ message: msg, isError });
    setTimeout(() => setFeedback(null), 4000);
  };

  const savePolicy = async () => {
    setSavingSection('policy');
    setError(null);
    try {
      const res = await axios.put(`${Config.APIBaseURL}/settings/security`, policy, {
        headers, withCredentials: true
      });
      setPolicy(res.data);
      flashFeedback('Password policy updated.');
    } catch (e) {
      flashFeedback(e.response?.data?.detail || 'Failed to save password policy', true);
    } finally {
      setSavingSection(null);
    }
  };

  const saveLockout = async () => {
    setSavingSection('lockout');
    setError(null);
    try {
      const res = await axios.put(`${Config.APIBaseURL}/settings/security/lockout`, lockout, {
        headers, withCredentials: true
      });
      setLockout(res.data);
      flashFeedback('Account lockout policy updated.');
    } catch (e) {
      flashFeedback(e.response?.data?.detail || 'Failed to save lockout policy', true);
    } finally {
      setSavingSection(null);
    }
  };

  const saveRequire2fa = async () => {
    setSavingSection('require_2fa');
    setError(null);
    try {
      const res = await axios.put(`${Config.APIBaseURL}/settings/security/require-2fa`,
        { require_2fa: require2fa }, { headers, withCredentials: true });
      setRequire2fa(res.data.require_2fa);
      flashFeedback('2FA enforcement policy updated.');
    } catch (e) {
      flashFeedback(e.response?.data?.detail || 'Failed to save 2FA policy', true);
    } finally {
      setSavingSection(null);
    }
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl text-slate-800 dark:text-slate-100 font-bold">Security Settings</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Configure system-wide password policy, account lockout, and 2FA enforcement.
            </p>
          </div>

          {feedback && (
            <div className={`mb-4 p-3 rounded text-sm ${feedback.isError ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'}`}>
              {feedback.message}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading security settings...</p>
          ) : (
            <>
              {/* Password Policy */}
              <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 border-l-4 border-l-amber-500 mb-6">
                <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Password Policy</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Rules enforced when users set or change their password.
                  </p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Min length (6-64)</label>
                      <input
                        type="number"
                        min="6" max="64"
                        value={policy.min_length}
                        onChange={(e) => setPolicy({ ...policy, min_length: parseInt(e.target.value) || 0 })}
                        className="form-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Max length (32-256)</label>
                      <input
                        type="number"
                        min="32" max="256"
                        value={policy.max_length}
                        onChange={(e) => setPolicy({ ...policy, max_length: parseInt(e.target.value) || 0 })}
                        className="form-input w-full"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    {[
                      { key: 'require_uppercase', label: 'Require uppercase letter' },
                      { key: 'require_lowercase', label: 'Require lowercase letter' },
                      { key: 'require_digits', label: 'Require digit' },
                      { key: 'require_special', label: 'Require special character' },
                    ].map(({ key, label }) => (
                      <label key={key} className="inline-flex items-center text-sm text-slate-700 dark:text-slate-300">
                        <input
                          type="checkbox"
                          className="form-checkbox mr-2"
                          checked={!!policy[key]}
                          onChange={(e) => setPolicy({ ...policy, [key]: e.target.checked })}
                        />
                        {label}
                      </label>
                    ))}
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                      Max repeated characters (1-10)
                    </label>
                    <input
                      type="number"
                      min="1" max="10"
                      value={policy.max_repeated_chars}
                      onChange={(e) => setPolicy({ ...policy, max_repeated_chars: parseInt(e.target.value) || 1 })}
                      className="form-input w-32"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={savePolicy}
                      disabled={savingSection === 'policy'}
                      className="btn bg-indigo-500 hover:bg-indigo-600 text-white text-sm disabled:opacity-50"
                    >
                      {savingSection === 'policy' ? 'Saving...' : 'Save Password Policy'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Account Lockout */}
              <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 border-l-4 border-l-rose-500 mb-6">
                <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Account Lockout</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Number of failed login attempts before an account is locked. Locked accounts must be unlocked via password reset.
                  </p>
                </div>
                <div className="p-6">
                  <div className="flex items-end gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Max failed attempts (1-50)
                      </label>
                      <input
                        type="number"
                        min="1" max="50"
                        value={lockout.max_failed_attempts}
                        onChange={(e) => setLockout({ max_failed_attempts: parseInt(e.target.value) || 1 })}
                        className="form-input w-32"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={saveLockout}
                      disabled={savingSection === 'lockout'}
                      className="btn bg-indigo-500 hover:bg-indigo-600 text-white text-sm disabled:opacity-50"
                    >
                      {savingSection === 'lockout' ? 'Saving...' : 'Save Lockout Policy'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Require 2FA */}
              <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 border-l-4 border-l-sky-500 mb-6">
                <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Two-Factor Authentication</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Choose whether 2FA is optional, required for administrators, or required for all users.
                  </p>
                </div>
                <div className="p-6">
                  <div className="flex items-end gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Enforcement
                      </label>
                      <select
                        value={require2fa}
                        onChange={(e) => setRequire2fa(e.target.value)}
                        className="form-select"
                      >
                        <option value="off">Optional (off)</option>
                        <option value="admins">Required for administrators</option>
                        <option value="all">Required for all users</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={saveRequire2fa}
                      disabled={savingSection === 'require_2fa'}
                      className="btn bg-indigo-500 hover:bg-indigo-600 text-white text-sm disabled:opacity-50"
                    >
                      {savingSection === 'require_2fa' ? 'Saving...' : 'Save 2FA Policy'}
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                    Note: this controls whether new logins are prompted to enable 2FA. Users who already have 2FA enabled are not affected.
                  </p>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default Security;
